import { JwtHelperService } from '@auth0/angular-jwt';
import { Serialized, SerializeUtil } from '../../../utils/serialize';

export const ACCESS_TOKEN_MEDIA_TYPE = 'application/vnd.io.idaas.api.access.token.v1+json';

export interface BackEndAccessToken {
  access_token: string;
  token_type: 'bearer';
  refresh_token?: string;
  /** Expiry time, in seconds. */
  expires_in: number;
}

export class AccessToken {
  @Serialized()
  public readonly innerToken: string;

  @Serialized({
    serialize: (date: Date) => date.toJSON(),
    deserialize: (dateString: string) => new Date(dateString),
  })
  public readonly expiresAt: Date;

  @Serialized()
  public readonly tokenType = 'Bearer';

  @Serialized()
  public readonly refreshToken?: string;

  constructor(innerToken?: string, expiresAt?: Date, refreshToken?: string) {
    this.innerToken = innerToken;
    this.expiresAt = expiresAt;
    this.refreshToken = refreshToken;
  }

  public static fromBackend(backendAccessToken: BackEndAccessToken): AccessToken {
    const expireTimeMs = backendAccessToken.expires_in * 1000;
    const expiresAt = new Date(Date.now() + expireTimeMs);
    return new AccessToken(backendAccessToken.access_token, expiresAt, backendAccessToken.refresh_token);
  }

  public toString(): string {
    return `${this.tokenType} ${this.innerToken}`;
  }

  public refreshTokenString(): string {
    return `${this.tokenType} ${this.refreshToken}`;
  }

  public serialize(): string {
    return SerializeUtil.deepSerialize(this);
  }

  public static deserialize(serialized: string | object): AccessToken {
    return SerializeUtil.deepDeserialize(new AccessToken(), serialized);
  }

  public isValid(): boolean {
    return this.isTokenValid(this.innerToken) || (!!this.refreshToken && this.isTokenValid(this.refreshToken));
  }

  private isTokenValid(token: string): boolean {
    const jwtHelperService = new JwtHelperService();
    try {
      return !jwtHelperService.isTokenExpired(token);
    } catch {
      return false;
    }
  }
}
